import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { db, logout } from "../config/firebase";
import {
    doc,
    getDoc,
    collection,
    getDocs,
    updateDoc,
    arrayUnion
} from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { SESSION_GROUP_ONLY_ENTRY } from '../constants/entryFlow';
import {
    BOOKING_CONTACT_REQUIRED_MESSAGE,
    bookingProfileBlockState,
    getBookingProfileBlockReason,
    isBookingProfileComplete,
} from '../utils/bookingContactRequirements';
import { isSignedInUser } from '../utils/authSession';

type CourseRef = { group: string; course: string };

interface ChooseCourseModalProps {
    showChooseCourseModal: boolean;
    setShowChooseCourseModal: (show: boolean) => void;
    user: User | null;
    authLoading: boolean;
    userData: any;
    userDataLoading: boolean;
    userDataError: string | null;
    /** After /group/:group login — list all courses in Firestore for this group, then add selection to profile. */
    pickFromGroupCatalog?: boolean;
    catalogGroupId: string | null;
    onGroupCatalogComplete?: () => void;
    refreshUserProfile?: () => void | Promise<void>;
    /** Bumps internal retry key in TeeTimes to re-run the initial profile Firestore read. */
    onRetryProfileLoad?: () => void;
}

function ChooseCourseModal({
    showChooseCourseModal,
    setShowChooseCourseModal,
    user,
    authLoading,
    userData,
    userDataLoading,
    userDataError,
    pickFromGroupCatalog = false,
    catalogGroupId,
    onGroupCatalogComplete,
    refreshUserProfile,
    onRetryProfileLoad
}: ChooseCourseModalProps) {
    const navigate = useNavigate();
    const [coursesData, setCoursesData] = useState<any[]>([]);
    const [coursesLoading, setCoursesLoading] = useState(false);
    const [coursesHydrated, setCoursesHydrated] = useState(false);
    const [coursesLoadError, setCoursesLoadError] = useState<string | null>(null);
    const [missingCourseRefs, setMissingCourseRefs] = useState<CourseRef[]>([]);
    const [profileSlowHint, setProfileSlowHint] = useState(false);
    const [coursesSlowHint, setCoursesSlowHint] = useState(false);
    const [savingCourseKey, setSavingCourseKey] = useState<string | null>(null);

    useEffect(() => {
        if (pickFromGroupCatalog) {
            return;
        }

        let cancelled = false;

        setCoursesHydrated(false);
        setCoursesLoadError(null);
        setMissingCourseRefs([]);
        setCoursesData([]);

        if (!userData?.courses?.length) {
            setCoursesLoading(false);
            setCoursesHydrated(true);
            return;
        }

        setCoursesLoading(true);

        const fetchCoursesData = async () => {
            const courseList = userData.courses as { group: string; course: string }[];
            const refKey = (g: string, c: string) => `${g}|${c}`;

            type FetchResult = {
                key: string;
                exists: boolean;
                data: any | null;
                error: string | null;
            };

            const uniqueKeys = new Set<string>();
            const toFetch: { group: string; course: string }[] = [];
            const missing: CourseRef[] = [];

            for (const course of courseList) {
                const groupId = course.group;
                const courseId = course.course;
                if (!groupId || !courseId) {
                    missing.push({ group: groupId ?? '', course: courseId ?? '' });
                    continue;
                }
                const k = refKey(groupId, courseId);
                if (uniqueKeys.has(k)) continue;
                uniqueKeys.add(k);
                toFetch.push({ group: groupId, course: courseId });
            }

            const fetchOne = async ({ group, course }: { group: string; course: string }): Promise<FetchResult> => {
                const k = refKey(group, course);
                try {
                    const courseRef = doc(db, 'groups', group, 'courses', course);
                    const courseDoc = await getDoc(courseRef);
                    if (courseDoc.exists()) {
                        const newData = courseDoc.data();
                        newData.id = course;
                        newData.group = group;
                        return { key: k, exists: true, data: newData, error: null };
                    }
                    return { key: k, exists: false, data: null, error: null };
                } catch (err: any) {
                    console.error('Error fetching course data:', err);
                    return { key: k, exists: false, data: null, error: err?.message || 'Unknown error' };
                }
            };

            const results = await Promise.all(toFetch.map(fetchOne));
            if (cancelled) return;

            const byKey = new Map<string, FetchResult>();
            for (const r of results) {
                byKey.set(r.key, r);
            }

            const aggregated: any[] = [];
            const errors: string[] = [];

            for (const course of courseList) {
                const groupId = course.group;
                const courseId = course.course;
                if (!groupId || !courseId) continue;
                const r = byKey.get(refKey(groupId, courseId));
                if (!r) continue;
                if (r.error) {
                    errors.push(r.error);
                    missing.push({ group: groupId, course: courseId });
                    continue;
                }
                if (r.exists && r.data) {
                    aggregated.push(r.data);
                } else {
                    missing.push({ group: groupId, course: courseId });
                }
            }

            setCoursesData(aggregated);
            setMissingCourseRefs(missing);
            if (errors.length) {
                setCoursesLoadError(Array.from(new Set(errors)).join('; '));
            }
            setCoursesLoading(false);
            setCoursesHydrated(true);
        };

        fetchCoursesData();
        return () => {
            cancelled = true;
        };
    }, [userData, pickFromGroupCatalog]);

    useEffect(() => {
        if (!pickFromGroupCatalog || !catalogGroupId || !user?.uid) {
            return;
        }

        let cancelled = false;
        setCoursesLoadError(null);
        setCoursesData([]);
        setMissingCourseRefs([]);
        setCoursesHydrated(false);
        setCoursesLoading(true);

        const run = async () => {
            try {
                const snap = await getDocs(collection(db, 'groups', catalogGroupId, 'courses'));
                if (cancelled) return;
                const list = snap.docs.map((d) => ({
                    id: d.id,
                    group: catalogGroupId,
                    ...d.data()
                }));
                setCoursesData(list);
            } catch (err: any) {
                if (!cancelled) {
                    console.error('Error loading group courses:', err);
                    setCoursesLoadError(err?.message || 'Could not load courses for this group.');
                }
            } finally {
                if (!cancelled) {
                    setCoursesLoading(false);
                    setCoursesHydrated(true);
                }
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [pickFromGroupCatalog, catalogGroupId, user?.uid]);

    useEffect(() => {
        const profileSpinnerVisible = !!user?.uid && userDataLoading;
        if (!profileSpinnerVisible) {
            setProfileSlowHint(false);
            return;
        }
        const id = window.setTimeout(() => setProfileSlowHint(true), 5000);
        return () => {
            window.clearTimeout(id);
            setProfileSlowHint(false);
        };
    }, [user?.uid, userDataLoading]);

    useEffect(() => {
        const catalogSpinner =
            pickFromGroupCatalog &&
            !!user?.uid &&
            !userDataLoading &&
            !!userData &&
            (!coursesHydrated || coursesLoading);

        const profileCoursesSpinner =
            !pickFromGroupCatalog &&
            !!user?.uid &&
            !userDataLoading &&
            !!(userData?.courses?.length) &&
            (!coursesHydrated || coursesLoading);

        const visible = catalogSpinner || profileCoursesSpinner;
        if (!visible) {
            setCoursesSlowHint(false);
            return;
        }
        const id = window.setTimeout(() => setCoursesSlowHint(true), 5000);
        return () => {
            window.clearTimeout(id);
            setCoursesSlowHint(false);
        };
    }, [
        pickFromGroupCatalog,
        user?.uid,
        userDataLoading,
        userData,
        userData?.courses,
        coursesHydrated,
        coursesLoading
    ]);

    const handleChooseCourse = (group: string, course: string) => {
        if (isSignedInUser(user) && userData && !isBookingProfileComplete(user, userData)) {
            handleGoToProfileForBooking();
            return;
        }
        localStorage.setItem('group', group);
        localStorage.setItem('course', course);
        setShowChooseCourseModal(false);
    };

    const handleCatalogCoursePick = async (group: string, course: string) => {
        if (!user?.uid) return;
        if (userData && !isBookingProfileComplete(user, userData)) {
            handleGoToProfileForBooking();
            return;
        }
        const key = `${group}|${course}`;
        setSavingCourseKey(key);
        setCoursesLoadError(null);
        try {
            const userRef = doc(db, 'users', user.uid);
            const snap = await getDoc(userRef);
            if (!snap.exists()) {
                setCoursesLoadError('No profile found for your account. Please register first.');
                return;
            }
            await updateDoc(userRef, {
                courses: arrayUnion({ group, course })
            });
            localStorage.setItem('group', group);
            localStorage.setItem('course', course);
            sessionStorage.removeItem(SESSION_GROUP_ONLY_ENTRY);
            onGroupCatalogComplete?.();
            await refreshUserProfile?.();
            setShowChooseCourseModal(false);
        } catch (err: any) {
            console.error('Error saving course to profile:', err);
            setCoursesLoadError(err?.message || 'Could not save course selection.');
        } finally {
            setSavingCourseKey(null);
        }
    };

    const handleLogoutClick = async (e?: React.MouseEvent) => {
        e?.preventDefault();
        await logout();
        const groupId = localStorage.getItem('group');
        navigate(groupId ? `/group/${encodeURIComponent(groupId)}` : '/', { replace: true });
    };

    const showAuthSpinner = authLoading;
    const showProfileSpinner = !!user?.uid && userDataLoading;
    const showCoursesSpinner =
        !pickFromGroupCatalog &&
        !!user?.uid &&
        !userDataLoading &&
        !!userData?.courses?.length &&
        (!coursesHydrated || coursesLoading);

    const catalogCoursesSpinner =
        pickFromGroupCatalog &&
        !!user?.uid &&
        !userDataLoading &&
        !!userData &&
        (!coursesHydrated || coursesLoading);

    const noLinkedCourses =
        !pickFromGroupCatalog &&
        isSignedInUser(user) &&
        !userDataLoading &&
        !userDataError &&
        userData &&
        (!userData.courses || userData.courses.length === 0);

    const profileMissing =
        isSignedInUser(user) && !userDataLoading && !userDataError && !userData;

    const bookingProfileIncomplete =
        isSignedInUser(user) &&
        !userDataLoading &&
        !userDataError &&
        !!userData &&
        !isBookingProfileComplete(user, userData);

    const bookingProfileBlockMessage = userData
        ? getBookingProfileBlockReason(user, userData)
        : null;

    const coursesFailedToResolve =
        !pickFromGroupCatalog &&
        isSignedInUser(user) &&
        !userDataLoading &&
        coursesHydrated &&
        !coursesLoading &&
        !!userData?.courses?.length &&
        coursesData.length === 0;

    const catalogEmpty =
        pickFromGroupCatalog &&
        isSignedInUser(user) &&
        !userDataLoading &&
        !!userData &&
        coursesHydrated &&
        !coursesLoading &&
        coursesData.length === 0;

    const handleGoToProfile = () => {
        navigate('/page/profile');
    };

    const handleGoToProfileForBooking = () => {
        const message =
            getBookingProfileBlockReason(user, userData) || BOOKING_CONTACT_REQUIRED_MESSAGE;
        navigate('/page/profile', { state: bookingProfileBlockState(message) });
    };

    const bookingProfileRequiredAlert = (
        <Alert variant="warning" className="text-start mb-0">
            <p className="mb-3">{bookingProfileBlockMessage}</p>
            <Button variant="primary" size="sm" type="button" onClick={handleGoToProfileForBooking}>
                Go to Account
            </Button>
        </Alert>
    );

    const profileMissingAlert = (
        <Alert variant="warning" className="text-start mb-0">
            <p className="mb-2">
                Your profile record is missing or incomplete. Finish your contact information on Account before you
                can book tee times.
            </p>
            <p className="small text-muted mb-3">
                If you recently switched devices or email, you can also log out and sign in again.
            </p>
            <div className="d-flex flex-wrap gap-2">
                <Button variant="primary" size="sm" type="button" onClick={handleGoToProfile}>
                    Go to Account / complete profile
                </Button>
                <Button variant="outline-secondary" size="sm" type="button" onClick={handleLogoutClick}>
                    Log out and try again
                </Button>
            </div>
        </Alert>
    );

    let body: React.ReactNode;

    if (showAuthSpinner) {
        body = (
            <div className="d-flex flex-column align-items-center py-4 gap-2">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mb-0 text-muted">Checking your session…</p>
            </div>
        );
    } else if (!isSignedInUser(user)) {
        body = (
            <p className="mb-0">
                Please sign in to see your courses and reserve a tee time.
            </p>
        );
    } else if (userDataError) {
        body = (
            <Alert variant="danger" className="text-start mb-0">
                {userDataError}
                <div className="small mt-2">
                    If this keeps happening, check your connection or try again later.
                </div>
                {onRetryProfileLoad && (
                    <div className="mt-3">
                        <Button variant="primary" size="sm" type="button" onClick={() => onRetryProfileLoad()}>
                            Try again
                        </Button>
                    </div>
                )}
            </Alert>
        );
    } else if (pickFromGroupCatalog) {
        if (showProfileSpinner) {
            body = (
                <div className="d-flex flex-column align-items-center py-4 gap-2">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mb-0 text-muted">Loading your profile…</p>
                    {profileSlowHint && (
                        <p className="small text-muted mb-0 px-2">
                            Sorry for the wait, this sometimes takes up to 20 seconds.
                        </p>
                    )}
                </div>
            );
        } else if (profileMissing) {
            body = profileMissingAlert;
        } else if (bookingProfileIncomplete) {
            body = bookingProfileRequiredAlert;
        } else if (catalogCoursesSpinner) {
            body = (
                <div className="d-flex flex-column align-items-center py-4 gap-2">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mb-0 text-muted">Loading your courses…</p>
                    {coursesSlowHint && (
                        <p className="small text-muted mb-0 px-2">
                            Sorry for the wait, this sometimes takes up to 20 seconds.
                        </p>
                    )}
                </div>
            );
        } else if (catalogEmpty) {
            body = (
                <Alert variant="warning" className="text-start mb-0">
                    No courses are published for this group yet. Please check back later or use your course&apos;s full reservation link.
                </Alert>
            );
        } else {
            body = (
                <>
                    {coursesLoadError && (
                        <Alert variant="danger" className="text-start">
                            {coursesLoadError}
                        </Alert>
                    )}
                    {coursesData.length > 0 &&
                        coursesData.map((courseData) => {
                            const key = `${courseData.group}-${courseData.id}`;
                            const busy = savingCourseKey === `${courseData.group}|${courseData.id}`;
                            return (
                                <Card
                                    className="mb-3"
                                    key={key}
                                    role="button"
                                    aria-disabled={!!savingCourseKey}
                                    onClick={() => {
                                        if (savingCourseKey) return;
                                        void handleCatalogCoursePick(courseData.group, courseData.id);
                                    }}
                                >
                                    <Card.Body>
                                        <Card.Title className="text-center">
                                            {courseData.name}
                                            {busy && (
                                                <span className="spinner-border spinner-border-sm ms-2" role="status" />
                                            )}
                                        </Card.Title>
                                        <Card.Text>
                                            {courseData.city && `${courseData.city}, `}
                                            {courseData.state && courseData.state}
                                            {courseData.province && courseData.province}
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            );
                        })}
                    <p className="small text-muted mb-0 mt-2 text-start">
                        Don&apos;t see your course? Try{' '}
                        <button
                            type="button"
                            className="btn btn-link btn-sm text-muted p-0 align-baseline text-decoration-underline"
                            onClick={handleLogoutClick}
                        >
                            logging out
                        </button>
                        {' '}
                        first, then if you still don&apos;t see it, open the reservation link from your course&apos;s website to add the course to your profile.
                    </p>
                </>
            );
        }
    } else if (showProfileSpinner) {
        body = (
            <div className="d-flex flex-column align-items-center py-4 gap-2">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mb-0 text-muted">Loading your profile…</p>
                {profileSlowHint && (
                    <p className="small text-muted mb-0 px-2">
                        Sorry for the wait, this sometimes takes up to 20 seconds.
                    </p>
                )}
            </div>
        );
    } else if (profileMissing) {
        body = profileMissingAlert;
    } else if (bookingProfileIncomplete) {
        body = bookingProfileRequiredAlert;
    } else if (noLinkedCourses) {
        body = (
            <>
                <p className="text-muted">No courses are linked to your account yet.</p>
                <p className="small mb-0">
                    Use your course&apos;s reservation link so we can attach the correct course to your profile.
                </p>
            </>
        );
    } else if (showCoursesSpinner) {
        body = (
            <div className="d-flex flex-column align-items-center py-4 gap-2">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mb-0 text-muted">Loading your courses…</p>
                {coursesSlowHint && (
                    <p className="small text-muted mb-0 px-2">
                        Sorry for the wait, this sometimes takes up to 20 seconds.
                    </p>
                )}
            </div>
        );
    } else {
        body = (
            <>
                {coursesLoadError && (
                    <Alert variant="danger" className="text-start">
                        Some course data could not be loaded: {coursesLoadError}
                    </Alert>
                )}
                {coursesFailedToResolve && (
                    <Alert variant="warning" className="text-start">
                        We could not load details for any of your linked courses.
                        {missingCourseRefs.length > 0 && (
                            <ul className="mb-0 mt-2 small text-start">
                                {missingCourseRefs.map((ref) => (
                                    <li key={`${ref.group}-${ref.course}`}>
                                        Group <code>{ref.group}</code>, course <code>{ref.course}</code> — document missing or inaccessible
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Alert>
                )}
                {coursesData.length > 0 &&
                    coursesData.map((courseData) => (
                        <Card
                            className="mb-3"
                            key={`${courseData.group}-${courseData.id}`}
                            role="button"
                            onClick={() => handleChooseCourse(courseData.group, courseData.id)}
                        >
                            <Card.Body>
                                <Card.Title className="text-center">{courseData.name}</Card.Title>
                                <Card.Text>
                                    {courseData.city && `${courseData.city}, `}
                                    {courseData.state && courseData.state}
                                    {courseData.province && courseData.province}
                                </Card.Text>
                            </Card.Body>
                        </Card>
                    ))}
                {coursesData.length > 0 && missingCourseRefs.length > 0 && (
                    <Alert variant="warning" className="text-start small">
                        Some links on your account could not be loaded:
                        <ul className="mb-0 mt-2">
                            {missingCourseRefs.map((ref) => (
                                <li key={`${ref.group}-${ref.course}`}>
                                    <code>{ref.group}</code> / <code>{ref.course}</code>
                                </li>
                            ))}
                        </ul>
                    </Alert>
                )}
                <p className="small text-muted mb-0 mt-2 text-start">
                    Don&apos;t see your course? Try{' '}
                    <button
                        type="button"
                        className="btn btn-link btn-sm text-muted p-0 align-baseline text-decoration-underline"
                        onClick={handleLogoutClick}
                    >
                        logging out
                    </button>
                    {' '}
                    first, then if you still don&apos;t see it, open the reservation link from your course&apos;s website to add the course to your profile.
                </p>
            </>
        );
    }

    return (
        <div className="modalContainer">
            <Modal id="confirmReservationModal" centered show={showChooseCourseModal} backdrop="static" className="text-center">
                <Modal.Header className="text-center d-block">
                    <Modal.Title>Where do you want to play?</Modal.Title>
                </Modal.Header>

                <Modal.Body>{body}</Modal.Body>
            </Modal>
        </div>
    );
}

export default ChooseCourseModal;
